// src/screens/RedfinAMVLookup.tsx
// Redfin AMV lookup - manual lookup OR continuous loop through states
// Data persists to database and can resume from where left off
// Results split into tabs: With AMV / No AMV / All

import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Alert,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SpeedIcon from '@mui/icons-material/Speed';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { apiFetch } from '../helpers';

interface LookupResult {
  id: number;
  propertyId: string;
  address: string;
  city: string | null;
  state: string | null;
  listPrice: number | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  amv: number | null;
  success: boolean;
  error: string | null;
  timeMs: number;
  isDeal: boolean;
  url?: string;
}

interface BofaRecord {
  _id: string;
  address: string;
  city: string | null;
  state: string | null;
  listPrice: number | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  amv: number | null;
  bofaAmv: number | null;
  bofaStatus: string | null;
  bofaError: string | null;
  amvError: string | null;
  bofaLookedUpAt: string | null;
  isDeal: boolean;
  createdAt: string;
}

interface Progress {
  currentStateIndex: number;
  currentState: string | null;
  processedStates: number;
  totalScraped: number;
  cycleCount: number;
  totalStates: number;
}

type ViewTab = 'all' | 'with_amv' | 'with_bofa' | 'no_amv' | 'bofa_queue';

const MAX_DISPLAYED = 200; // Only keep last 200 rows in the DOM to prevent UI freezing

const currency = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt = (n?: number | null) => (typeof n === 'number' && n > 0 ? currency.format(n) : '—');

export default function RedfinAMVLookup() {
  // Manual lookup state
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Loop state
  const [loopRunning, setLoopRunning] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [pollSince, setPollSince] = useState<number | null>(null);
  const pollSinceRef = useRef<number>(0); // mutable ref so setInterval always reads latest
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Results
  const [results, setResults] = useState<LookupResult[]>([]);
  const [idCounter, setIdCounter] = useState(1);

  // Stats
  const [stats, setStats] = useState({ total: 0, success: 0, noAmv: 0, deals: 0 });

  // Progress
  const [progress, setProgress] = useState<Progress | null>(null);

  // DISABLED: Realtor AMV step removed — addresses go directly from Redfin → BofA
  // const [realtorRunning, setRealtorRunning] = useState(false);
  // const [realtorStatusText, setRealtorStatusText] = useState('');
  // const [realtorStats, setRealtorStats] = useState({ pending: 0, success: 0, failed: 0, skipped: 0 });
  // const [realtorRecords, setRealtorRecords] = useState<BofaRecord[]>([]);
  // const [realtorRecordsTotal, setRealtorRecordsTotal] = useState(0);
  // const [realtorRecordsLoading, setRealtorRecordsLoading] = useState(false);
  // const realtorPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // BofA state
  const [bofaRunning, setBofaRunning] = useState(false);
  const [bofaStatus, setBofaStatus] = useState('');
  const [bofaStats, setBofaStats] = useState({ totalNoAmv: 0, bofaPending: 0, bofaSuccess: 0, bofaFailed: 0, bofaNoData: 0, bofaSkip: 0, bofaProcessed: 0 });
  const [bofaMovedDeals, setBofaMovedDeals] = useState(0);
  const [bofaMovedAllAddr, setBofaMovedAllAddr] = useState(0);
  const [bofaSkippedExisting, setBofaSkippedExisting] = useState(0);
  const [bofaTab, setBofaTab] = useState(0);
  const [bofaRecords, setBofaRecords] = useState<BofaRecord[]>([]);
  const [bofaRecordsTotal, setBofaRecordsTotal] = useState(0);
  const [bofaRecordsLoading, setBofaRecordsLoading] = useState(false);
  const bofaPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // View tab
  const [viewTab, setViewTab] = useState<ViewTab>('all');

  // Unified display rows per tab (fetched from the right endpoint)
  const [displayRows, setDisplayRows] = useState<any[]>([]);
  const [displayLoading, setDisplayLoading] = useState(false);
  const [displayTotal, setDisplayTotal] = useState(0);

  const tableRef = useRef<HTMLDivElement>(null);

  // Fetch correct data per tab
  useEffect(() => {
    let cancelled = false;
    async function loadForTab() {
      setDisplayLoading(true);
      try {
        let rows: any[] = [];
        let total = 0;
        if (viewTab === 'all' || viewTab === 'with_amv') {
          const tabParam = viewTab === 'with_amv' ? 'with_amv' : '';
          const res = await apiFetch(`/api/redfin-amv-lookup/results?tab=${tabParam}&limit=200`);
          const data = await res.json();
          if (data.success) {
            rows = (data.results || []).map((r: any) => ({
              address: r.fullAddress || r.address,
              state: r.state,
              listPrice: r.listingPrice || r.listPrice,
              amv: r.amv || r.redfinAmv,
              beds: r.beds,
              baths: r.baths,
              sqft: r.sqft,
              isDeal: r.isDeal,
              amvSource: r.amvSource,
              bofaStatus: null,
              error: null,
              url: r.url,
            }));
            total = data.stats?.total || rows.length;
          }
        } else {
          const bofaTabParam =
            viewTab === 'with_bofa' ? 'bofa_only' :
            viewTab === 'no_amv' ? 'no_amv_all' :
            'pending';
          const res = await apiFetch(`/api/redfin-amv-lookup/bofa/records?tab=${bofaTabParam}&limit=200`);
          const data = await res.json();
          if (data.success) {
            const recs = data.records || [];
            rows = recs.map((r: any) => ({
              address: r.address,
              state: r.state,
              listPrice: r.listPrice || r.listingPrice,
              amv: r.bofaAmv || r.amv,
              beds: r.beds,
              baths: r.baths,
              sqft: r.sqft,
              isDeal: r.isDeal,
              amvSource: r.amvSource,
              bofaStatus: r.bofaStatus,
              error: r.amvError || r.bofaError,
              url: r.url,
              bofaFetchedAt: r.bofaFetchedAt,
              updatedAt: r.updatedAt,
            }));
            total = data.total || rows.length;
          }
        }
        if (!cancelled) {
          setDisplayRows(rows);
          setDisplayTotal(total);
        }
      } catch (err) {
        console.error('Tab data fetch error:', err);
      } finally {
        if (!cancelled) setDisplayLoading(false);
      }
    }
    loadForTab();
    return () => { cancelled = true; };
  }, [viewTab]);

  // Legacy filter (kept for hidden old UI still referencing filteredResults)
  const filteredResults = useMemo(() => {
    if (viewTab === 'with_amv') {
      return results.filter(r => r.success && r.amv && r.amv > 0);
    }
    if (viewTab === 'no_amv') {
      return results.filter(r => !r.success || !r.amv || r.amv === 0);
    }
    return results;
  }, [results, viewTab]);

  // Load persisted data on page load
  useEffect(() => {
    loadPersistedData();
  }, []);

  const loadPersistedData = async () => {
    setInitialLoading(true);
    try {
      // Fetch stored results and progress in parallel
      const [resultsRes, progressRes] = await Promise.all([
        apiFetch('/api/redfin-amv-lookup/results'),
        apiFetch('/api/redfin-amv-lookup/progress'),
      ]);

      const resultsData = await resultsRes.json();
      const progressData = await progressRes.json();

      if (resultsData.success && resultsData.results) {
        // Convert stored results to LookupResult format
        const loadedResults: LookupResult[] = resultsData.results.map((r: any, idx: number) => ({
          id: idx + 1,
          propertyId: r.propertyId,
          address: r.fullAddress || r.address,
          city: r.city,
          state: r.state,
          listPrice: r.listPrice,
          beds: r.beds,
          baths: r.baths,
          sqft: r.sqft,
          amv: r.amv,
          success: r.success,
          error: r.error,
          timeMs: r.timeMs || 0,
          isDeal: r.isDeal,
          url: r.url,
        }));

        setResults(loadedResults);
        setIdCounter(loadedResults.length + 1);
        setStats(resultsData.stats || { total: 0, success: 0, noAmv: 0, deals: 0 });

        if (loadedResults.length > 0) {
          setCurrentStatus(`Loaded ${loadedResults.length} results from database`);
        }
      }

      if (progressData.success && progressData.progress) {
        setProgress(progressData.progress);
      }

      // Check if loop is currently running (handles page refresh)
      const statusRes = await apiFetch('/api/redfin-amv-lookup/status');
      const statusData = await statusRes.json();
      if (statusData.isRunning) {
        setLoopRunning(true);
        setCurrentStatus(statusData.status || 'Running...');
        const since = Date.now() - 60000; // Look back 1 minute
        setPollSince(since);
        startPolling(since);
      }
    } catch (err: any) {
      console.error('Failed to load persisted data:', err);
    } finally {
      setInitialLoading(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (tableRef.current && loopRunning) {
      tableRef.current.scrollTop = tableRef.current.scrollHeight;
    }
  }, [filteredResults, loopRunning]);

  // Manual fetch
  const handleManualFetch = async () => {
    if (!input.trim()) {
      setError('Please enter a Redfin URL or Property ID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await apiFetch('/api/redfin-amv-lookup/fetch', {
        method: 'POST',
        body: JSON.stringify({ input: input.trim() }),
      });

      const data = await res.json();

      const isDeal = data.success && data.listPrice &&
        data.amv >= (data.listPrice / 0.55) && data.amv > 200000;

      const newResult: LookupResult = {
        id: idCounter,
        propertyId: data.propertyId || '',
        address: data.fullAddress || data.address || input.trim(),
        city: data.city || null,
        state: data.state || null,
        listPrice: data.listPrice || null,
        beds: data.beds || null,
        baths: data.baths || null,
        sqft: data.sqft || null,
        amv: data.amv || null,
        success: data.success,
        error: data.error || null,
        timeMs: data.timeMs || 0,
        isDeal,
      };

      setResults(prev => [newResult, ...prev].slice(0, MAX_DISPLAYED));
      setIdCounter(prev => prev + 1);
      setStats(prev => ({
        total: prev.total + 1,
        success: prev.success + (data.success && data.amv ? 1 : 0),
        noAmv: prev.noAmv + (!data.success || !data.amv ? 1 : 0),
        deals: prev.deals + (isDeal ? 1 : 0),
      }));
      setInput('');
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch AMV');
    } finally {
      setLoading(false);
    }
  };

  // Poll for updates from the background job (called every 3s)
  const pollForUpdates = async () => {
    try {
      const res = await apiFetch(`/api/redfin-amv-lookup/poll?since=${pollSinceRef.current}`);
      const data = await res.json();
      if (!data.success) return;

      setCurrentStatus(data.status || '');

      // Append new results
      if (data.newResults && data.newResults.length > 0) {
        setResults(prev => {
          const existingIds = new Set(prev.map(r => r.propertyId));
          const newOnes = data.newResults
            .filter((r: any) => !existingIds.has(r.propertyId))
            .map((r: any, idx: number) => ({
              id: prev.length + idx + 1,
              propertyId: r.propertyId,
              address: r.fullAddress || r.address,
              city: r.city,
              state: r.state,
              listPrice: r.listPrice,
              beds: r.beds,
              baths: r.baths,
              sqft: r.sqft,
              amv: r.amv,
              success: r.success,
              error: r.error,
              timeMs: r.timeMs || 0,
              isDeal: r.isDeal,
              url: r.url,
            }));
          const combined = [...prev, ...newOnes];
          // Keep only the last MAX_DISPLAYED rows to prevent UI freeze
          return combined.length > MAX_DISPLAYED ? combined.slice(-MAX_DISPLAYED) : combined;
        });

        // Move the "since" cursor forward to the newest result
        const newest = data.newResults[data.newResults.length - 1];
        if (newest?.createdAt) {
          pollSinceRef.current = new Date(newest.createdAt).getTime();
        }
      }

      if (data.stats) setStats(data.stats);
      if (data.progress) setProgress(data.progress);

      // DISABLED: Realtor AMV step removed
      // if (data.realtor) {
      //   setRealtorRunning(data.realtor.isRunning || false);
      //   if (data.realtor.status) setRealtorStatusText(data.realtor.status);
      // }

      // Update BofA status from poll response
      if (data.bofa) {
        setBofaRunning(data.bofa.isRunning || false);
        if (data.bofa.status) setBofaStatus(data.bofa.status);
        if (data.bofa.stats) setBofaStats(data.bofa.stats);
        if (typeof data.bofa.movedToDeals === 'number') setBofaMovedDeals(data.bofa.movedToDeals);
        if (typeof data.bofa.movedToAllAddr === 'number') setBofaMovedAllAddr(data.bofa.movedToAllAddr);
        if (typeof data.bofa.skippedExisting === 'number') setBofaSkippedExisting(data.bofa.skippedExisting);
      }

      // Stop polling if the backend loop finished
      if (!data.isRunning) {
        setLoopRunning(false);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    } catch (err: any) {
      console.error('Poll error:', err);
    }
  };

  // Helper to start the polling interval
  const startPolling = (since: number) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollSinceRef.current = since;
    pollIntervalRef.current = setInterval(pollForUpdates, 3000);
  };

  // Start loop (continuous - runs until stopped)
  const startLoop = async () => {
    setError(null);

    try {
      const res = await apiFetch('/api/redfin-amv-lookup/start-loop', {
        method: 'POST',
      });
      const data = await res.json();

      if (!data.success) {
        setError(data.message || 'Failed to start loop');
        return;
      }

      setLoopRunning(true);
      setCurrentStatus('Starting continuous loop...');

      const since = Date.now();
      setPollSince(since);
      startPolling(since);
    } catch (err: any) {
      setError(err?.message || 'Failed to start loop');
    }
  };

  // Stop loop
  const stopLoop = async () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    try {
      await apiFetch('/api/redfin-amv-lookup/stop', { method: 'POST' });
    } catch (err) {
      // Ignore
    }

    setLoopRunning(false);
    setCurrentStatus('Stopped — Click "Start Loop" to resume');
  };

  // Reset everything (start fresh)
  const resetAll = async () => {
    if (loopRunning) {
      await stopLoop();
    }

    try {
      await apiFetch('/api/redfin-amv-lookup/reset', { method: 'POST' });

      setResults([]);
      setStats({ total: 0, success: 0, noAmv: 0, deals: 0 });
      setIdCounter(1);
      setProgress(null);
      setPollSince(null);
      setCurrentStatus('Reset complete — Ready to start fresh');
    } catch (err: any) {
      setError(err?.message || 'Failed to reset');
    }
  };

  const clearResultsView = () => {
    // Only clears the view, not the database
    setResults([]);
    setStats({ total: 0, success: 0, noAmv: 0, deals: 0 });
    setIdCounter(1);
  };

  // ─── DISABLED: Realtor AMV handlers — addresses go directly from Redfin → BofA ───

  // const fetchRealtorStatus = async () => {
  //   try {
  //     const [statusRes, statsRes] = await Promise.all([
  //       apiFetch('/api/redfin-amv-lookup/realtor/status'),
  //       apiFetch('/api/redfin-amv-lookup/realtor/stats'),
  //     ]);
  //     const statusData = await statusRes.json();
  //     const statsData = await statsRes.json();
  //     if (statusData.success) {
  //       setRealtorRunning(statusData.isRunning || false);
  //       setRealtorStatusText(statusData.currentStatus || '');
  //     }
  //     if (statsData.success && statsData.stats) {
  //       setRealtorStats(statsData.stats);
  //     }
  //   } catch (err) { console.error('Realtor status error:', err); }
  // };

  // const startRealtorLoop = async () => { ... };
  // const stopRealtorLoop = async () => { ... };
  // const fetchRealtorRecords = async (tab?: number) => { ... };
  // useEffect(() => { fetchRealtorStatus(); ... }, []);
  // useEffect(() => { ... }, [realtorRunning]);

  // ─── BofA handlers ───

  const fetchBofaStatus = async () => {
    try {
      const res = await apiFetch('/api/redfin-amv-lookup/bofa/status');
      const data = await res.json();
      if (data.success) {
        setBofaRunning(data.isProcessing || false);
        setBofaStatus(data.currentStatus || '');
        if (data.stats) setBofaStats(data.stats);
        if (typeof data.movedToDeals === 'number') setBofaMovedDeals(data.movedToDeals);
        if (typeof data.movedToAllAddr === 'number') setBofaMovedAllAddr(data.movedToAllAddr);
        if (typeof data.skippedExisting === 'number') setBofaSkippedExisting(data.skippedExisting);
      }
    } catch (err) {
      console.error('BofA status error:', err);
    }
  };

  const startBofaLoop = async () => {
    try {
      const res = await apiFetch('/api/redfin-amv-lookup/bofa/start', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setBofaRunning(true);
        setBofaStatus('Starting...');
        // Start polling BofA status every 5s
        if (bofaPollRef.current) clearInterval(bofaPollRef.current);
        bofaPollRef.current = setInterval(fetchBofaStatus, 5000);
      } else {
        setError(data.message || 'Failed to start BofA');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to start BofA');
    }
  };

  const stopBofaLoop = async () => {
    try {
      await apiFetch('/api/redfin-amv-lookup/bofa/stop', { method: 'POST' });
      setBofaStatus('Stopping...');
    } catch (err) {
      // ignore
    }
  };

  // Map tab index to API tab param (re-indexed after removing Realtor tabs 1-2)
  const bofaTabToApi = (tab: number): string => {
    switch (tab) {
      case 0: return 'success';       // Have AMV (all sources)
      case 1: return 'bofa_success';  // With BofA AMV only
      case 2: return 'queue';         // BofA Queue
      case 3: return 'done_skip';     // Don't Need BofA
      case 4: return 'pending';       // BofA Progress
      case 5: return 'pending';       // Pipeline
      default: return 'pending';
    }
  };

  const fetchBofaRecords = async (tab?: number) => {
    const apiTab = bofaTabToApi(tab ?? bofaTab);
    setBofaRecordsLoading(true);
    try {
      const res = await apiFetch(`/api/redfin-amv-lookup/bofa/records?tab=${apiTab}&limit=200`);
      const data = await res.json();
      if (data.success) {
        setBofaRecords(data.records || []);
        setBofaRecordsTotal(data.total || 0);
      }
    } catch (err) {
      console.error('BofA records error:', err);
    } finally {
      setBofaRecordsLoading(false);
    }
  };

  // Reload records when tab changes
  useEffect(() => {
    if (bofaTab <= 4) {
      fetchBofaRecords(bofaTab);
    }
  }, [bofaTab]);

  // Load BofA status on mount + cleanup
  useEffect(() => {
    fetchBofaStatus();
    return () => {
      if (bofaPollRef.current) clearInterval(bofaPollRef.current);
    };
  }, []);

  // Start/stop BofA polling when running state changes
  useEffect(() => {
    if (bofaRunning) {
      if (!bofaPollRef.current) {
        bofaPollRef.current = setInterval(fetchBofaStatus, 5000);
      }
    } else {
      if (bofaPollRef.current) {
        clearInterval(bofaPollRef.current);
        bofaPollRef.current = null;
      }
    }
  }, [bofaRunning]);

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getSpeedColor = (ms: number) => {
    if (ms < 400) return '#22c55e';
    if (ms < 800) return '#f59e0b';
    return '#ef4444';
  };

  if (initialLoading) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ mr: 2 }} />
        <Typography>Loading saved results...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header — Redfin Properties */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#111' }}>
          Redfin Properties
        </Typography>
      </Box>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {/* Stats Cards (visible) */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Total</Typography>
          <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">With AMV</Typography>
          <Typography variant="h5" fontWeight={700} color="success.main">{stats.success}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">No AMV</Typography>
          <Typography variant="h5" fontWeight={700} color="warning.main">{stats.noAmv}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Deals</Typography>
          <Typography variant="h5" fontWeight={700} color="primary.main">{stats.deals}</Typography>
        </Paper>
        {progress && progress.processedStates > 0 && (
          <Paper variant="outlined" sx={{ p: 2, minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary">Progress</Typography>
            <Typography variant="h6" fontWeight={700}>
              {progress.processedStates}/{progress.totalStates} states
            </Typography>
          </Paper>
        )}
      </Box>

      {/* ==== OLD SECTIONS HIDDEN FOR REVIEW — set to true to restore ==== */}
      {false && (<>

      {/* Controls Section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {/* Loop Controls */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Continuous Loop — Scrapes all states, restarts automatically, runs until you stop
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {!loopRunning ? (
              <Button
                variant="contained"
                color="success"
                startIcon={<PlayArrowIcon />}
                onClick={startLoop}
                sx={{ fontWeight: 600 }}
              >
                {progress && progress.processedStates > 0 ? 'Resume Loop' : 'Start Loop'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={stopLoop}
                sx={{ fontWeight: 600 }}
              >
                Stop Loop
              </Button>
            )}
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RestartAltIcon />}
              onClick={resetAll}
              disabled={loopRunning}
              sx={{ fontWeight: 600 }}
            >
              Reset All
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadPersistedData}
              disabled={loopRunning}
            >
              Reload Data
            </Button>
            {currentStatus && (
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                {loopRunning && <CircularProgress size={14} sx={{ mr: 1 }} />}
                {currentStatus}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* BofA Lookup for No-AMV Records (Realtor step removed) */}
        <Box sx={{ mb: 2 }}>
          {/* Header: title + BofA controls + status */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              AMV Fallback Lookup
            </Typography>
            {/* DISABLED: Realtor buttons — addresses go directly from Redfin → BofA */}
            {/* {!realtorRunning ? (
              <Button variant="contained" size="small" startIcon={<PlayArrowIcon />} onClick={startRealtorLoop}
                sx={{ fontWeight: 600, bgcolor: '#ea580c', '&:hover': { bgcolor: '#c2410c' } }}>Start Realtor</Button>
            ) : (
              <Button variant="contained" color="error" size="small" startIcon={<StopIcon />} onClick={stopRealtorLoop}
                sx={{ fontWeight: 600 }}>Stop Realtor</Button>
            )} */}
            {!bofaRunning ? (
              <Button
                variant="contained"
                color="info"
                size="small"
                startIcon={<PlayArrowIcon />}
                onClick={startBofaLoop}
                disabled={loopRunning}
                sx={{ fontWeight: 600 }}
              >
                Start BofA
              </Button>
            ) : (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<StopIcon />}
                onClick={stopBofaLoop}
                sx={{ fontWeight: 600 }}
              >
                Stop BofA
              </Button>
            )}
            {bofaStatus && (
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                {bofaRunning && <CircularProgress size={14} sx={{ mr: 1 }} />}
                {bofaStatus}
              </Typography>
            )}
          </Box>

          {/* Tabs (Realtor tabs removed, re-indexed) */}
          <Tabs
            value={bofaTab}
            onChange={(_, v) => setBofaTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 36, mb: 1.5, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontWeight: 600, fontSize: 13 } }}
          >
            <Tab label={`Have AMV (${(stats.success + bofaStats.bofaSuccess).toLocaleString()})`} />
            <Tab label={`BofA Processed (${(bofaStats.bofaSuccess + bofaStats.bofaNoData + bofaStats.bofaFailed).toLocaleString()})`} />
            <Tab label={`BofA Queue (${bofaStats.bofaPending.toLocaleString()})`} />
            <Tab label={`Don't Need BofA (${(bofaStats.bofaSkip + bofaStats.bofaNoData + bofaStats.bofaFailed).toLocaleString()})`} />
            <Tab label="BofA Progress" />
            <Tab label="Pipeline (→ Deals)" />
          </Tabs>

          {/* Tabs 0-4: BofA/combined table with summary header */}
          {(bofaTab >= 0 && bofaTab <= 4) && (
            <Box>
              {/* Summary chips row */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                {bofaTab === 0 && (
                  <>
                    <Chip label={`Redfin: ${stats.success.toLocaleString()}`} size="small" color="success" variant="outlined" />
                    <Chip label={`BofA: ${bofaStats.bofaSuccess.toLocaleString()}`} size="small" sx={{ bgcolor: '#2563eb', color: 'white' }} />
                    <Chip label={`Total: ${(stats.success + bofaStats.bofaSuccess).toLocaleString()}`} size="small" sx={{ bgcolor: '#16a34a', color: 'white', fontWeight: 600 }} />
                    <Chip label={`Deals: ${stats.deals}`} size="small" color="primary" />
                  </>
                )}
                {bofaTab === 1 && (
                  <>
                    <Chip label={`AMV Found: ${bofaStats.bofaSuccess.toLocaleString()}`} size="small" sx={{ bgcolor: '#22c55e', color: 'white', fontWeight: 600 }} />
                    <Chip label={`No Data: ${bofaStats.bofaNoData.toLocaleString()}`} size="small" sx={{ bgcolor: '#f59e0b', color: 'white', fontWeight: 600 }} />
                    <Chip label={`Failed: ${bofaStats.bofaFailed.toLocaleString()}`} size="small" sx={{ bgcolor: '#ef4444', color: 'white', fontWeight: 600 }} />
                    <Chip label="All records processed by BofA" size="small" variant="outlined" />
                  </>
                )}
                {bofaTab === 2 && (
                  <>
                    <Chip label={`In Queue: ${bofaStats.bofaPending.toLocaleString()}`} size="small" sx={{ bgcolor: '#2563eb', color: 'white', fontWeight: 600 }} />
                    <Chip label="Waiting for BofA browser lookup" size="small" variant="outlined" />
                  </>
                )}
                {bofaTab === 3 && (
                  <>
                    <Chip label={`Skipped: ${bofaStats.bofaSkip.toLocaleString()}`} size="small" variant="outlined" />
                    <Chip label={`No Data: ${bofaStats.bofaNoData.toLocaleString()}`} size="small" color="warning" variant="outlined" />
                    <Chip label={`Failed: ${bofaStats.bofaFailed.toLocaleString()}`} size="small" color="error" variant="outlined" />
                    <Chip label={`Total: ${(bofaStats.bofaSkip + bofaStats.bofaNoData + bofaStats.bofaFailed).toLocaleString()}`} size="small" sx={{ bgcolor: '#6b7280', color: 'white', fontWeight: 600 }} />
                  </>
                )}
                {bofaTab === 4 && (
                  <>
                    <Chip label={`Pending: ${bofaStats.bofaPending.toLocaleString()}`} size="small" variant="outlined" />
                    <Chip label={`AMV Found: ${bofaStats.bofaSuccess.toLocaleString()}`} size="small" color="success" variant="outlined" />
                    <Chip label={`No Data: ${bofaStats.bofaNoData.toLocaleString()}`} size="small" color="warning" variant="outlined" />
                    <Chip label={`Failed: ${bofaStats.bofaFailed.toLocaleString()}`} size="small" color="error" variant="outlined" />
                    <Chip label={`Processed: ${bofaStats.bofaProcessed.toLocaleString()}`} size="small" variant="outlined" />
                  </>
                )}
                <Button size="small" startIcon={<RefreshIcon />} onClick={() => fetchBofaRecords()} sx={{ ml: 'auto' }}>
                  Refresh
                </Button>
              </Box>

              {/* Address table */}
              <TableContainer sx={{ maxHeight: 350, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#111827', color: 'white', fontWeight: 600, fontSize: 12 } }}>
                      <TableCell>Address</TableCell>
                      <TableCell sx={{ width: 50 }}>State</TableCell>
                      <TableCell sx={{ width: 90 }} align="right">List Price</TableCell>
                      <TableCell sx={{ width: 90 }} align="right">AMV</TableCell>
                      <TableCell sx={{ width: 80 }} align="center">Beds/Bath</TableCell>
                      <TableCell sx={{ width: 70 }} align="right">Sqft</TableCell>
                      <TableCell sx={{ width: 80 }} align="center">Status</TableCell>
                      <TableCell sx={{ width: 60 }} align="center">Deal?</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bofaRecordsLoading && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={20} sx={{ mr: 1 }} /> Loading records...
                        </TableCell>
                      </TableRow>
                    )}
                    {!bofaRecordsLoading && bofaRecords.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No records found
                        </TableCell>
                      </TableRow>
                    )}
                    {!bofaRecordsLoading && bofaRecords.map((rec) => (
                      <TableRow
                        key={rec._id}
                        sx={{
                          bgcolor: rec.isDeal ? '#f0fdf4' : 'inherit',
                          '&:nth-of-type(odd)': { bgcolor: rec.isDeal ? '#f0fdf4' : '#f9fafb' },
                          '&:hover': { bgcolor: rec.isDeal ? '#dcfce7' : '#f3f4f6' },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>
                            {rec.address}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize={12}>{rec.state || '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontSize={12} sx={{ color: '#166534', fontWeight: 600 }}>
                            {fmt(rec.listPrice)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontSize={12} sx={{ color: rec.bofaAmv ? '#7c3aed' : (rec as any).realtorAmv ? '#ea580c' : rec.amv ? '#166534' : '#991b1b', fontWeight: 600 }}>
                            {fmt(rec.bofaAmv || (rec as any).realtorAmv || rec.amv)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontSize={12}>
                            {rec.beds && rec.baths ? `${rec.beds} / ${rec.baths}` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontSize={12}>
                            {rec.sqft ? rec.sqft.toLocaleString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" title={rec.amvError || rec.bofaError || undefined}>
                          <Chip
                            label={rec.bofaStatus === 'failed' ? (rec.amvError || 'Failed') : rec.bofaStatus === 'no_data' ? 'No Data' : rec.bofaStatus || ((rec as any).realtorStatus === 'success' ? 'realtor' : (rec as any).realtorStatus || (rec.amv ? 'redfin' : 'pending'))}
                            size="small"
                            sx={{
                              fontSize: 10,
                              height: 20,
                              fontWeight: 600,
                              maxWidth: 120,
                              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                              bgcolor: rec.bofaStatus === 'success' ? '#dcfce7' : rec.bofaStatus === 'no_data' ? '#fef3c7' : rec.bofaStatus === 'failed' ? '#fee2e2' : rec.bofaStatus === 'skip' ? '#f3f4f6' : (rec as any).realtorStatus === 'success' ? '#fff7ed' : rec.amv ? '#dcfce7' : '#dbeafe',
                              color: rec.bofaStatus === 'success' ? '#166534' : rec.bofaStatus === 'no_data' ? '#92400e' : rec.bofaStatus === 'failed' ? '#991b1b' : rec.bofaStatus === 'skip' ? '#6b7280' : (rec as any).realtorStatus === 'success' ? '#9a3412' : rec.amv ? '#166534' : '#1e40af',
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {rec.isDeal && (
                            <Chip label="DEAL" size="small" sx={{ bgcolor: '#22c55e', color: 'white', fontWeight: 600, fontSize: 10, height: 20 }} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {bofaRecordsTotal > bofaRecords.length && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Showing {bofaRecords.length} of {bofaRecordsTotal.toLocaleString()} records
                </Typography>
              )}
            </Box>
          )}

          {/* DISABLED: Realtor AMV tabs removed — entire Realtor records table commented out */}

          {/* Tab 5: Pipeline — where records go after BofA */}
          {bofaTab === 5 && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 120, textAlign: 'center', border: '2px solid #22c55e' }}>
                <Typography variant="caption" sx={{ color: '#16a34a' }}>→ Deals Page</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#16a34a' }}>{bofaMovedDeals.toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary">dealStage: new</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 120, textAlign: 'center', border: '2px solid #3b82f6' }}>
                <Typography variant="caption" sx={{ color: '#2563eb' }}>→ All Addresses</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#2563eb' }}>{bofaMovedAllAddr.toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary">dealStage: all_addresses</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 120, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Skipped (Protected)</Typography>
                <Typography variant="h5" fontWeight={700} color="text.secondary">{bofaSkippedExisting.toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary">already in pipeline</Typography>
              </Paper>
              <Box sx={{ ml: 2, flex: 1, minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  How it works:
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.6 }}>
                  BofA finds AMV + LP &le; 55% of AMV + AMV &gt; $200k → <strong style={{ color: '#16a34a' }}>Deal Page</strong><br/>
                  No AMV or not a deal → <strong style={{ color: '#2563eb' }}>All Addresses</strong><br/>
                  Already in email_sent / follow_up / deal_status → <strong>Skipped</strong>
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Manual Lookup */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Manual Lookup — Single Property
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              label="Redfin URL or Property ID"
              placeholder="5509005 or https://www.redfin.com/.../home/5509005"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !loading && handleManualFetch()}
              disabled={loading || loopRunning}
              size="small"
              sx={{ flex: 1, minWidth: 300 }}
            />
            <Button
              variant="outlined"
              onClick={handleManualFetch}
              disabled={loading || loopRunning || !input.trim()}
              startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
            >
              Lookup
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* OLD View Mode Toggle — commented for review
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={viewTab}
          exclusive
          onChange={(_, newTab) => newTab && setViewTab(newTab)}
          size="small"
        >
          <ToggleButton value="with_amv" sx={{ textTransform: 'none', fontWeight: 600 }}>
            With AMV ({stats.success})
          </ToggleButton>
          <ToggleButton value="no_amv" sx={{ textTransform: 'none', fontWeight: 600 }}>
            No AMV ({stats.noAmv})
          </ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 600 }}>
            All ({stats.total})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      */}

      </>)}
      {/* ==== END HIDDEN OLD SECTIONS ==== */}

      {/* NEW 5-Tab Toggle — Zillow-style */}
      <div style={{ marginBottom: 16 }}>
        <ToggleButtonGroup
          value={viewTab}
          exclusive
          onChange={(_, newTab) => newTab && setViewTab(newTab)}
          size="small"
        >
          <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 700 }}>
            All Results ({stats.total.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="with_amv" sx={{ textTransform: 'none', fontWeight: 600, color: viewTab === 'with_amv' ? '#166534' : undefined }}>
            With AMV (Redfin) ({stats.success.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="with_bofa" sx={{ textTransform: 'none', fontWeight: 600, color: viewTab === 'with_bofa' ? '#fff' : '#7c3aed', '&.Mui-selected': { bgcolor: '#7c3aed', color: '#fff', '&:hover': { bgcolor: '#6d28d9' } } }}>
            With BofA AMV ({bofaStats.bofaSuccess.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="no_amv" sx={{ textTransform: 'none', fontWeight: 600, color: viewTab === 'no_amv' ? '#b45309' : undefined }}>
            No AMV ({(bofaStats.bofaNoData + bofaStats.bofaFailed).toLocaleString()})
          </ToggleButton>
          <ToggleButton value="bofa_queue" sx={{ textTransform: 'none', fontWeight: 600, color: viewTab === 'bofa_queue' ? '#1d4ed8' : undefined }}>
            BofA Queue ({bofaStats.bofaPending.toLocaleString()})
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {/* OLD Results Table — disabled for review (set to true to restore) */}
      {false && (
      <Paper variant="outlined">
        <Box sx={{
          p: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#f9fafb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {viewTab === 'with_amv' ? 'Properties With AMV' : viewTab === 'no_amv' ? 'Properties Without AMV' : 'All Results'}
            {filteredResults.length > 0 && ` (${filteredResults.length})`}
            {stats.total > results.length && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Showing latest {results.length} of {stats.total.toLocaleString()} total in database
              </Typography>
            )}
          </Typography>
          {results.length > 0 && (
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={clearResultsView}
              sx={{ color: '#6b7280' }}
            >
              Clear View
            </Button>
          )}
        </Box>

        <TableContainer ref={tableRef} sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 40 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 40 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 80 }}>State</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">List Price</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">AMV</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 70 }} align="center">Deal?</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 70 }} align="right">Speed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResults.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {results.length === 0
                      ? 'Click "Start Loop" to scrape states automatically, or use manual lookup above'
                      : `No results in "${viewTab === 'with_amv' ? 'With AMV' : viewTab === 'no_amv' ? 'No AMV' : 'All'}" tab`
                    }
                  </TableCell>
                </TableRow>
              )}
              {filteredResults.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    bgcolor: row.isDeal ? '#f0fdf4' : row.success ? 'inherit' : '#fef2f2',
                    '&:hover': { bgcolor: row.isDeal ? '#dcfce7' : row.success ? '#f9fafb' : '#fee2e2' },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{row.id}</Typography>
                  </TableCell>
                  <TableCell>
                    {row.success ? (
                      <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 18 }} />
                    ) : (
                      <ErrorIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.address}
                    </Typography>
                    {row.beds && (
                      <Typography variant="caption" color="text.secondary">
                        {row.beds} bed • {row.baths} bath {row.sqft && `• ${row.sqft.toLocaleString()} sqft`}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.state || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{fmt(row.listPrice)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: row.success ? '#166534' : '#991b1b' }}
                    >
                      {row.success ? fmt(row.amv) : row.error}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {row.isDeal && (
                      <Chip
                        label="DEAL"
                        size="small"
                        sx={{
                          bgcolor: '#22c55e',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 10,
                          height: 20,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: getSpeedColor(row.timeMs) }}
                    >
                      {formatTime(row.timeMs)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}
      {/* END old table */}

      {/* NEW Zillow-style table */}
      <div
        style={{
          overflowX: 'auto',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, color: '#111' }}>
            {viewTab === 'all' && `All Results (${stats.total.toLocaleString()})`}
            {viewTab === 'with_amv' && `With AMV Redfin (${stats.success.toLocaleString()})`}
            {viewTab === 'with_bofa' && `With BofA AMV (${bofaStats.bofaSuccess.toLocaleString()})`}
            {viewTab === 'no_amv' && `No AMV (${(bofaStats.bofaNoData + bofaStats.bofaFailed).toLocaleString()})`}
            {viewTab === 'bofa_queue' && `BofA Queue (${bofaStats.bofaPending.toLocaleString()})`}
          </span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            Showing {displayRows.length}{displayTotal > displayRows.length ? ` of ${displayTotal.toLocaleString()}` : ''} rows
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr style={{ background: '#111827', color: '#fff' }}>
              {['Address', 'State', 'List Price', viewTab === 'with_bofa' ? 'BofA AMV' : 'AMV', 'Beds/Baths', 'Sqft', 'Deal?', viewTab === 'with_bofa' ? 'BofA Fetched' : 'Status'].map((h, i) => (
                <th
                  key={h}
                  style={{
                    textAlign: i === 0 ? 'left' : 'right',
                    padding: '12px 14px',
                    fontSize: 12,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayLoading && (
              <tr>
                <td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  Loading...
                </td>
              </tr>
            )}
            {!displayLoading && displayRows.map((row, i) => {
              const zebra = i % 2 === 0 ? '#ffffff' : '#f9fafb';
              const bg = row.isDeal ? '#f0fdf4' : zebra;
              const hasAmv = row.amv && row.amv > 0;
              return (
                <tr key={i} style={{ background: bg }}>
                  <td style={{ padding: 14, minWidth: 280 }}>
                    <div style={{ fontWeight: 600, color: '#111' }}>
                      {row.url ? (
                        <a href={row.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>
                          {row.address || '—'}
                        </a>
                      ) : (
                        row.address || '—'
                      )}
                    </div>
                  </td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>{row.state || '—'}</td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(row.listPrice)}</td>
                  <td style={{ padding: 14, textAlign: 'right', color: hasAmv ? '#7c3aed' : '#991b1b', fontWeight: 600 }}>
                    {hasAmv ? fmt(row.amv) : (row.error || '—')}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>
                    {row.beds || '—'} / {row.baths || '—'}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>
                    {row.sqft ? row.sqft.toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    <Chip
                      label={row.isDeal ? 'DEAL' : 'No'}
                      size="small"
                      sx={{
                        backgroundColor: row.isDeal ? '#dcfce7' : '#f3f4f6',
                        color: row.isDeal ? '#166534' : '#6b7280',
                        fontWeight: 600,
                      }}
                    />
                  </td>
                  <td style={{ padding: 14, textAlign: 'right', fontSize: 13, color: '#6b7280' }}>
                    {viewTab === 'with_bofa' ? (
                      row.bofaFetchedAt ? new Date(row.bofaFetchedAt).toLocaleString() :
                      row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'
                    ) : (
                      <Chip
                        label={row.bofaStatus || row.amvSource || (hasAmv ? 'ok' : '—')}
                        size="small"
                        sx={{
                          backgroundColor:
                            row.bofaStatus === 'success' || row.amvSource === 'bofa' ? '#ede9fe' :
                            row.amvSource === 'redfin' ? '#dcfce7' :
                            row.bofaStatus === 'pending' ? '#dbeafe' :
                            row.bofaStatus === 'no_data' ? '#fef3c7' :
                            row.bofaStatus === 'failed' ? '#fee2e2' : '#f3f4f6',
                          color:
                            row.bofaStatus === 'success' || row.amvSource === 'bofa' ? '#6d28d9' :
                            row.amvSource === 'redfin' ? '#166534' :
                            row.bofaStatus === 'pending' ? '#1d4ed8' :
                            row.bofaStatus === 'no_data' ? '#92400e' :
                            row.bofaStatus === 'failed' ? '#991b1b' : '#6b7280',
                          fontWeight: 600,
                          fontSize: 10,
                        }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
            {!displayLoading && displayRows.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  {viewTab === 'all' && 'No results yet. Start the loop or use manual lookup.'}
                  {viewTab === 'with_amv' && 'No Redfin AMV results yet.'}
                  {viewTab === 'with_bofa' && 'No BofA AMVs found yet. Run BofA lookup.'}
                  {viewTab === 'no_amv' && 'All addresses have AMVs. Caught up!'}
                  {viewTab === 'bofa_queue' && 'Queue is empty.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Box>
  );
}
